import React, { useState } from "react";
import { X, Play, Loader2, Terminal, Code2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const SAMPLE = `# Sandboxed Python (no network, 8s limit, 256MB)
import statistics
data = [12, 7, 22, 5, 19, 30]
print("mean:", statistics.mean(data))
print("stdev:", round(statistics.pstdev(data), 3))
`;

export default function DeveloperPanel({ open, onClose }) {
  const [code, setCode] = useState(SAMPLE);
  const [stdin, setStdin] = useState("");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const run = async () => {
    try {
      setBusy(true); setOut(null);
      const r = await api.developerRun({ code, stdin });
      setOut(r);
      if (!r.ok) toast.message("Program exited with a non-zero status.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execution failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B0F17] flex flex-col" data-testid="developer-panel">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400"><Code2 size={18} /></div>
          <div>
            <div className="font-head font-bold text-white">Developer AI · Python Sandbox</div>
            <div className="text-[11px] text-slate-500 flex items-center gap-1"><ShieldCheck size={12} className="text-emerald-400" /> Isolated subprocess · network & process-spawn disabled · CPU/memory/time limited</div>
          </div>
        </div>
        <button data-testid="close-developer-panel" onClick={onClose} className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:text-white"><X size={18} /></button>
      </div>

      <div className="flex-1 overflow-hidden grid lg:grid-cols-2 gap-0">
        <div className="flex flex-col border-r border-white/10 min-h-0">
          <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-slate-500 border-b border-white/10">Code</div>
          <textarea data-testid="developer-code-input" value={code} onChange={(e) => setCode(e.target.value)} spellCheck={false}
            className="flex-1 bg-[#0B0F17] text-emerald-100 font-mono text-[13px] p-4 resize-none focus:outline-none leading-relaxed" />
          <div className="px-4 py-2 border-t border-white/10">
            <input data-testid="developer-stdin-input" value={stdin} onChange={(e) => setStdin(e.target.value)} placeholder="stdin (optional)"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 mb-2" />
            <button data-testid="developer-run-button" onClick={run} disabled={busy}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Run
            </button>
          </div>
        </div>

        <div className="flex flex-col min-h-0">
          <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-slate-500 border-b border-white/10 flex items-center gap-1.5"><Terminal size={12} /> Output</div>
          <div className="flex-1 overflow-y-auto p-4 font-mono text-[13px]" data-testid="developer-output">
            {!out && <div className="text-slate-600">Run your code to see the output.</div>}
            {out && (
              <>
                <div className={`mb-2 text-[11px] ${out.ok ? "text-emerald-400" : "text-red-400"}`}>exit code: {out.exitCode} {out.ok ? "(success)" : "(error)"}</div>
                {out.stdout && <pre className="whitespace-pre-wrap text-slate-100 mb-3">{out.stdout}</pre>}
                {out.stderr && <pre className="whitespace-pre-wrap text-red-300">{out.stderr}</pre>}
                {!out.stdout && !out.stderr && <div className="text-slate-600">(no output)</div>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
